import type { ITaskPullRequest } from './githubPullRequest'
import type { ITaskEmbeddedUser } from './task'

// Asana-style user-defined custom fields for Atlas Tasks. A field is defined once
// in a reusable library (ICustomFieldDefinition) and later attached to one or
// more projects; each task then stores its own value per attached field.

// The supported field types. Formula and dependent/conditional fields are
// explicitly out of scope (see the task's Scope & Decisions).
export enum CustomFieldType {
  Text = 'Text',
  Number = 'Number',
  SingleSelect = 'SingleSelect',
  MultiSelect = 'MultiSelect',
  Date = 'Date',
  People = 'People',
  // Linked GitHub pull requests. Belong to the task, not a value row: every field of
  // this type shows the same list (ITaskCustomFieldEntry.pullRequests).
  GitHubPullRequests = 'GitHubPullRequests',
}

// Display formatting for Number fields; ignored for every other type.
export enum CustomFieldNumberFormat {
  Plain = 'Plain',
  Currency = 'Currency',
  Percentage = 'Percentage',
}

// Prefix marking a filter/sort key as targeting a custom field rather than a
// task column (`cf_<uuid>`). Shared so the filter configs, the client's
// filter/sort serialisation, and the server's query builder agree on one form.
export const CUSTOM_FIELD_KEY_PREFIX = 'cf_'

export const customFieldKey = (definitionId: string): string =>
  `${CUSTOM_FIELD_KEY_PREFIX}${definitionId}`

// The select-type set — the only types that own options.
export const SELECT_CUSTOM_FIELD_TYPES = [
  CustomFieldType.SingleSelect,
  CustomFieldType.MultiSelect,
] as const

// A select option, stored inline in the definition's `options` jsonb array (not
// its own table) — a bounded, parent-owned config list, matching the codebase's
// jsonb-array pattern (e.g. client_info.payoff_fees).
export interface ICustomFieldOption {
  // Stable uuid assigned on creation; task values reference it by id.
  id: string
  name: string
  // Palette key (matches the option color set used elsewhere in the app).
  color: string
  // Ascending display order within the field.
  position: number
  // Options are soft-disabled rather than deleted so historical task values that
  // reference a removed option still resolve. Disabled options are hidden from
  // new value entry but shown (greyed) where already set.
  isEnabled: boolean
  // Seeded option that code writes by name (e.g. the Category field's workflow
  // values, assigned by the task syncs). Its name is a contract, so renaming,
  // removing, or disabling it is rejected — colour and position stay editable.
  // Users can still add their own options to the same field.
  isSystem?: boolean
}

export interface ICustomFieldDefinition {
  id: string
  name: string
  description: string | null
  type: CustomFieldType
  // Number-only config; null for non-number fields.
  numberFormat: CustomFieldNumberFormat | null
  precision: number | null
  createdByUserId: string | null
  // Select-field options (empty for non-select types).
  options: ICustomFieldOption[]
  // Applies to every task regardless of project attachment. System-owned
  // (seeded, e.g. Category) — never settable through the library UI.
  isGlobal: boolean
  // Projects this field is attached to (first-attached first). Populated by the
  // library list so the Custom Fields table can show "<first> +N".
  projects?: { id: string; name: string }[]
  createdAt: string
  updatedAt: string
}

// Payload for creating/updating an option inside a definition write. Existing
// options carry their id; new ones omit it.
export interface ICustomFieldOptionInput {
  id?: string
  name: string
  color: string
  position: number
  isEnabled?: boolean
}

// A custom-field definition attached to a project. `position` and `isHidden`
// (column visibility) are per-project; `customFieldDefinition` is the resolved
// library definition.
export interface ITaskProjectCustomField {
  id: string
  projectId: string
  customFieldDefinitionId: string
  position: number
  isHidden: boolean
  customFieldDefinition?: ICustomFieldDefinition
  createdAt: string
  updatedAt: string
}

// A task's stored value for one field. Exactly one of the typed columns is
// meaningful per the field's type (see CustomFieldType).
export interface ITaskCustomFieldValue {
  id: string
  taskId: string
  customFieldDefinitionId: string
  textValue: string | null
  numberValue: number | null
  dateValue: string | null
  selectedOptionIds: string[] | null
  selectedUserIds: string[] | null
  // Resolved display info for `selectedUserIds` (People fields), populated on
  // read — includes deactivated users so their name still renders.
  selectedUsers?: ITaskEmbeddedUser[]
  // Teams on a People field; each reaches its current members.
  selectedTeamIds: string[] | null
  // Resolved names for `selectedTeamIds`, including deleted teams.
  selectedTeams?: ICustomFieldSelectedTeam[]
  createdAt: string
  updatedAt: string
}

// The value's data alone, unset parts omitted: what a list row carries per field.
export type ITaskCustomFieldValueData = Partial<
  Omit<
    ITaskCustomFieldValue,
    'id' | 'taskId' | 'customFieldDefinitionId' | 'createdAt' | 'updatedAt'
  >
>

// The value payload accepted by the set-value endpoint. Callers send only the
// key matching the field's type; the rest are cleared.
export interface ICustomFieldValueInput {
  textValue?: string | null
  numberValue?: number | null
  dateValue?: string | null
  selectedOptionIds?: string[] | null
  selectedUserIds?: string[] | null
  selectedTeamIds?: string[] | null
}

export interface ICustomFieldSelectedTeam {
  id: string
  name: string
}

// One of the task's projects that attached a field, at that project's column position.
export interface ITaskCustomFieldProjectAttachment {
  projectId: string
  position: number
}

// A field applicable to a task (definition + whether it's required on the task's
// project) paired with the task's current value, for the task detail panel.
export interface ITaskCustomFieldEntry<TValue = ITaskCustomFieldValue> {
  definition: ICustomFieldDefinition
  value: TValue | null
  // Set by the task detail only; empty for a global field, which applies through `isGlobal`.
  projectAttachments?: ITaskCustomFieldProjectAttachment[]
  // GitHubPullRequests fields only.
  pullRequests?: ITaskPullRequest[]
}
